import { z } from "zod";
import { IsoDateTime, OpaqueId, Seq } from "./common.js";
import { RegulationOptionKind, ReleveRisk } from "./regulation.js";
export const DeviationPriority = z.enum(["high", "normal"]);
export const DeviationKind = z.enum([
    "late",
    "early",
    "bunching",
    "stale",
    "no-realtime",
    "releve-risk", // P2-18 — relève / pause légale menacée par le retard
]);
/**
 * Acquittement d'un écart — marqueur interne au poste (« vu, traité »).
 * N'agit ni sur le réseau ni sur le bord. L'auteur enregistré est LE POSTE,
 * pas la personne (CDC §7.3). C'est, avec l'administration, la seule écriture
 * de toute la console de régulation.
 */
export const DeviationAck = z.object({
    acked: z.boolean(),
    byPost: z.string().nullable(),
    at: IsoDateTime.nullable(),
});
export const Deviation = z.object({
    id: OpaqueId,
    priority: DeviationPriority,
    kind: DeviationKind,
    lineId: OpaqueId,
    lineCode: z.string(),
    direction: z.union([z.literal(0), z.literal(1)]),
    headsign: z.string(),
    journeyId: OpaqueId,
    // nullable : un écart "no-realtime" porte sur une course dont aucun véhicule
    // n'a jamais été identifié en temps réel (CDC §3.2).
    vehicleDisplayNumber: z.string().nullable(),
    runId: z.string().nullable(),
    serviceId: z.string().nullable(),
    depotId: OpaqueId.nullable(), // D-007 — toujours null
    atStopId: OpaqueId.nullable(),
    atStopName: z.string().nullable(),
    deviationSeconds: z.number().int(),
    since: IsoDateTime,
    ack: DeviationAck,
    /**
     * P2-18 — options de régulation applicables à cette course (badge « voir
     * options » ⇒ `GET /journeys/{id}/regulation-options` pour le détail chiffré).
     * `hold`/`early-departure` portent aussi un effet chiffré ici, cf.
     * `holdSeconds`/`earlyDepartureSeconds`.
     */
    suggestionKinds: z.array(RegulationOptionKind),
    /** Secondes à tenir au terminus pour régulariser (avance / bunching) — `null` si sans objet. */
    holdSeconds: z.number().int().nullable(),
    /** Secondes de retard résorbables en repartant sans battement — `null` si sans objet. */
    earlyDepartureSeconds: z.number().int().nullable(),
    /** P2-18 — relève / pause menacée (renseigné surtout quand `kind: "releve-risk"`). */
    releveRisk: ReleveRisk.nullable(),
});
/** File d'écarts — déjà triée (priorité, puis écart décroissant) et seuillée côté serveur. */
export const DeviationsResponse = z.object({
    at: IsoDateTime,
    seq: Seq, // D-009
    deviations: z.array(Deviation),
});
// `post` est requis sur les deux formes tant que l'authentification par poste
// (B7.2) n'est pas branchée : pas de session pour le déduire côté serveur.
export const DeviationAckRequest = z.union([
    z.object({ ids: z.array(OpaqueId).min(1), post: z.string() }),
    z.object({ all: z.literal(true), post: z.string() }),
]);
export const DeviationAckResponse = z.object({ acked: z.array(OpaqueId) });
//# sourceMappingURL=deviation.js.map